import type { SelfClaimsSnapshot } from "../../src/student-view-contract";
import { PetMarks } from "./pet-stars-ui";
import { shownMarks } from "../../src/pet-stars";
import { MARKET_CONTENT } from "../../src/i18n/market-content";
import { featurePath, marketingPagesFor, toolPath } from "../../src/marketing-pages";
import { REFERRAL_RULES } from "../../src/referral-rules";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { formBox, loadedPetForms, loadPetForm, petFormUrl } from "./pet-evolution-forms";
import { clarityEvent, tourEventName } from "./clarity";
import { LOCALE_HEADER, localeFromLanguages, localeFromPath } from "../../src/i18n/locales";
import { EXPORT_MAX_DOWNLOADS, EXPORT_WINDOW_DAYS } from "../../src/data-export-policy";
import { msg } from "../../src/i18n/translate";
import { FULL_LOCALES, LOCALE_CONFIG, LOCALES, type Locale, basePath, browserTimezone, isDefaultLocale, isPublicSitePath, locale, lp, pageExistsIn, pathInLocale, savedLocale, switchLocale, t, tx } from "./i18n";
import { type BoardModule } from "../../src/board-layout";
import { type ClassroomShortcuts } from "../../src/classroom-shortcuts";
import { type PetIdleMotion } from "../../src/pet-idle";
import { growthPointsOf, PET_SPECIES, PET_SPECIES_NAMES, type PetMode, type PetPickMode, type PetSpecies } from "../../src/pet-modes";
import { badgeAppliesTo, type BadgeKind, type PrizeRarity, type RewardKind, type SurprisePrize } from "../../src/store";
import { type ForestPetSpecies } from "./forest-pet-scene";
import type { createTourSession } from "./tour-session";
import { usePetAtlasSurface } from "./pet-atlas-surface";
import type { petEvolutionData } from "./pet-evolution-data";
import { petEvolutionClipUrls } from "./pet-evolution-manifest";
import { decodeRosterText, type RosterTextEncoding } from "../../src/roster-csv";
import { type PlanTier } from "../../src/plan-policy";
import { canTidyClass, isPlanErrorCode, isWritableState, PLAN_ERROR_EVENT, type OverLimit, type PlanErrorDetail } from "./plan-access";
import "./styles.css";
import "./forest-ui.css";
import "./home-contrast.css";
import "./home-stats.css";
import "./classroom-gathering.css";

// The draw globe is only used by 抽籤點名; load it with that tool.
export const DrawGlobe = React.lazy(() => import("./draw-globe").then((module) => ({ default: module.DrawGlobe })));
// The in-memory tour adapter (tour-session + tour-records) ships only with /tour.
export let createTourSessionFn: typeof createTourSession | null = null;
export const loadTourSession = () => import("./tour-session").then((module) => { createTourSessionFn = module.createTourSession; });
export const HelpCenter = React.lazy(() => import("./help-center"));

// Public tours share the product UI, but never persist visitor changes.
export const publicTourPage = basePath === "/tour";
export const tourPreferenceCache = new Map<string, string>();
export const tourTabCache = new Map<string, string>();
/** /tour, or a page showing an in-memory demo classroom (the home-page live demo): keep nothing in the browser. */
export const inMemoryDemo = () => publicTourPage || tourSessions.size > 0;
export function transientStorage(storage: Storage, cache: Map<string, string>): Pick<Storage, "getItem" | "setItem" | "removeItem"> {
  return {
    getItem: (key) => inMemoryDemo() ? cache.get(key) ?? null : storage.getItem(key),
    setItem: (key, value) => { if (inMemoryDemo()) cache.set(key, value); else storage.setItem(key, value); },
    removeItem: (key) => { if (inMemoryDemo()) cache.delete(key); else storage.removeItem(key); },
  };
}
export const preferenceStorage = transientStorage(localStorage, tourPreferenceCache);
export const tabStorage = transientStorage(sessionStorage, tourTabCache);
export type TourSession = ReturnType<typeof createTourSession<Snapshot>>;
export const tourSessions = new Map<string, TourSession>();

export type Student = {
  id: string;
  display_name: string;
  seat_no: string | null;
  avatar_key: string;
  /** Versioned URL of the teacher-uploaded photo (D-112); null shows the preset avatar. */
  photo_url?: string | null;
  group_id: string | null;
  group_name: string | null;
  color_key: string | null;
  points_earned?: number;
  points_spent?: number;
  points_available?: number;
};
export type ArchivedStudent = Pick<Student, "id" | "display_name" | "seat_no" | "avatar_key"> & { archived_at: string };
export type Group = { id: string; name: string; color_key: string; sort_order: number };
export type Tag = { id: string; name: string; emoji: string; points: number; polarity: 'positive' | 'negative'; note: string; /** D-209: children may pick this reason for a self-claim (1) or not (0/absent). */ self_claim?: number };

export { ForestIcon, type ForestIconName } from "./forest-icon";
import { ForestIcon, type ForestIconName } from "./forest-icon";

export function BrandMark({ className = "" }: { className?: string }) {
  return <img className={`brand-mark-image ${className}`} src="/assets/forest-ui/brand/classmori-mark.webp" alt="" aria-hidden="true" />;
}

// Spanish has its own template (scripts/build-roster-templates.mjs); others get the zh/en bilingual one.
const ROSTER_TEMPLATES: Partial<Record<Locale, string>> = { "pt-BR": "/ClassMori-Student-Roster-pt-BR", "tr": "/ClassMori-Student-Roster-tr", "vi": "/ClassMori-Student-Roster-vi", "id": "/ClassMori-Student-Roster-id",  "zh-Hans": "/ClassMori-Student-Roster-zh-CN", es: "/ClassMori-Plantilla-Lista-de-la-Clase" };
export function RosterTemplateDownloads() {
  const base = ROSTER_TEMPLATES[locale] ?? "/ClassMori-Student-Roster-Template";
  return <div className="roster-template-downloads" aria-label={t("下載學生名單範本")}>
    <a download href={`${base}.xlsx`}>{t("下載 Excel 範本 .xlsx")}</a>
    <a download href={`${base}.xls`}>{t("舊版 Excel .xls")}</a>
    <a download href={`${base}.ods`}>ODS</a>
    <a download href={`${base}.csv`}>CSV</a>
  </div>;
}

export const isTextRosterFile = (name: string) => /\.(csv|tsv|txt)$/i.test(name);
export async function readRosterWorkbook(file: File, encoding: RosterTextEncoding) {
  const XLSX = await import("@e965/xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = isTextRosterFile(file.name)
    ? XLSX.read(decodeRosterText(buffer, encoding, locale), { type: "string" })
    : XLSX.read(buffer, { type: "array" });
  return Object.fromEntries(workbook.SheetNames.map((name) => [name, XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[name], { header: 1, raw: false, defval: "" })]));
}

// Links into the teacher app carry the page language (?lang=en) so it survives the host change.
// On the public site the page language is always passed on (Chinese too), so an older
// English cookie never turns the Chinese site's sign-up into English.
const withLang = (path: string) => {
  const fromPublicSite = location.hostname !== "app.classmori.com" && isPublicSitePath(basePath);
  if (isDefaultLocale && !fromPublicSite) return path;
  const [pathname, hash = ""] = path.split("#");
  return `${pathname}${pathname.includes("?") ? "&" : "?"}lang=${locale}${hash ? `#${hash}` : ""}`;
};
export const teacherHref = (path: string) => location.hostname === "classmori.com" ? `https://app.classmori.com${withLang(path)}` : withLang(path);
/** Public site link in the current language (/pricing → /en/pricing on the English site). */
export const publicHref = (path: string) => location.hostname === "app.classmori.com" ? `https://classmori.com${lp(path)}` : lp(path);

// Hand-drawn icons replace only the untouched built-in reasons (exact name +
// emoji), so a teacher's own ⭐ item always shows the emoji they picked.
export const DEFAULT_REASON_ICONS: Record<string, ForestIconName> = {
  // Stored reason names (data, not UI text).
  "主動幫忙|🤝": "help", "小組合作|🌿": "cooperate", "專心上課|👀": "focus", "專心上堂|👀": "focus", // i18n-ignore
  "舉手發言|✋": "raise-hand", "舉手回答|✋": "raise-hand", "完成挑戰|⭐": "challenge", // i18n-ignore
  "準時交作業|✓": "homework", "準時交功課|✓": "homework", // i18n-ignore
  // English built-in reasons (seeded by the Worker for English classes).
  "Helping others|🤝": "help", "Teamwork|🌿": "cooperate", "Focused in class|👀": "focus",
  "Raising hand|✋": "raise-hand", "Challenge complete|⭐": "challenge", "Homework on time|✓": "homework",
  // Spanish built-in reasons (src/i18n/messages/es; kept in sync by src/i18n/i18n.test.ts).
  "Ayuda a los demás|🤝": "help", "Trabajo en equipo|🌿": "cooperate", "Atención en clase|👀": "focus",
  "Levanta la mano|✋": "raise-hand", "Reto cumplido|⭐": "challenge", "Tarea a tiempo|✓": "homework",
  // French built-in reasons (src/starter-sets.ts; the first two kept their earlier names in older classes).
  "Entraide|🤝": "help", "Aide aux autres|🤝": "help", "Travail d'équipe|🌿": "cooperate", "Bonne concentration|👀": "focus",
  "Lever la main|✋": "raise-hand", "Défi réussi|⭐": "challenge", "Devoirs faits|✓": "homework", "Devoirs à l'heure|✓": "homework",
};
export function feedbackIcon(name: string, emoji: string): ForestIconName | null {
  return DEFAULT_REASON_ICONS[`${name}|${emoji}`] ?? null;
}
export function FeedbackReasonContent({ tag }: { tag: Tag }) {
  const icon = feedbackIcon(tag.name, tag.emoji);
  return <>{icon ? <ForestIcon name={icon} /> : <span className="custom-tag-emoji">{tag.emoji || '✦'}</span>}<span className="reason-label"><strong>{!isDefaultLocale && DEFAULT_REASON_ICONS[`${tag.name}|${tag.emoji ?? ""}`] ? t(tag.name) : tag.name}</strong><small>{tag.polarity === 'positive' ? '+' : '−'}{t("{count} 點", { count: tag.points })}</small></span></>;
}

/** Public top navigation; a function so the labels are translated at render time.
 * 關於我們 lives in the footer only, so the header stays short (2026-10-07). */
export const publicNavItems = (): ReadonlyArray<{ href: string; label: string; badge?: string }> => [
  { href: "/", label: t("首頁") },
  // The free tools hub (SEO entry point): a friendly name plus a 免費 tag so the header says what it is.
  { href: "/tools", label: t("教師工具箱"), badge: t("免費") },
  // 教學專欄 (D-270): long-form answers to teachers' questions; each article leads on to a free tool.
  { href: "/articles", label: t("教學專欄") },
  { href: "/tour", label: t("示範教室") },
  { href: "/pricing", label: t("方案價格") },
  { href: "/contact", label: tx("nav", "聯絡我們") },
];

/** The current page is the item, or (for the tools hub) one of its tool pages. */
const navCurrent = (href: string) => basePath === href ? "page" : (href === "/tools" || href === "/articles") && basePath.startsWith(`${href}/`) ? "true" : undefined;
const NavLinks = () => <>{publicNavItems().map(({ href, label, badge }) => <a key={href} href={publicHref(href)} aria-current={navCurrent(href)}>{label}{badge && <em className="nav-badge">{badge}</em>}</a>)}</>;

/** Every public page, /tools and the help center included, shows the same banner and header (2026-10-11). */
/** Arrived through a colleague's invite link (/r/CODE adds ?invited=1, D-176): remembered for this tab only. */
const invitedVisit = () => {
  try {
    if (new URLSearchParams(location.search).get("invited") === "1") sessionStorage.setItem("classmori-invited", "1");
    return sessionStorage.getItem("classmori-invited") === "1";
  } catch { return new URLSearchParams(location.search).get("invited") === "1"; }
};

export function PublicSalesLayout({ children }: { children: React.ReactNode }) {
  const ribbon = useRef<HTMLElement>(null);
  const [invited] = useState(invitedVisit);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const measure = () => root.style.setProperty("--sales-ribbon-height", `${ribbon.current?.getBoundingClientRect().height ?? 0}px`);
    root.classList.add("public-sales-mode");
    measure();
    const observer = new ResizeObserver(measure);
    if (ribbon.current) observer.observe(ribbon.current);
    return () => {
      observer.disconnect();
      root.classList.remove("public-sales-mode");
      root.style.removeProperty("--sales-ribbon-height");
    };
  }, []);
  return <div className="public-sales-layout">
    <aside ref={ribbon} className={`launch-ribbon${invited ? " launch-ribbon-invited" : ""}`} aria-label={t("免費使用與方案")}>
      <span className="launch-ribbon-copy">{invited
        ? t("同事邀請你一起用小森課堂：免費註冊，在班上用起來再多送 {days} 天標準版", { days: REFERRAL_RULES.qualifyInviteeDays })
        : t("免費版永久可用，不需信用卡")}</span>
      <a className="launch-ribbon-cta" href={teacherHref("/register")}>{t("免費開始使用")}</a>
    </aside>
    <LocaleSuggestion />
    {children}
  </div>;
}

export function PublicNav() {
  return <nav className="public-nav" aria-label={t("公開頁面")}>
    <NavLinks />
  </nav>;
}


/** Free tools the footer lists: the most opened on the public site (public_tool_usage_daily 2026-10-26–11-03, GSC agrees;
 * D-278). A language without one of them (light languages publish a subset) moves on down the list. */
const FOOTER_TOOLS = ["noise-meter", "scoreboard", "guess-number", "random-picker", "timer", "seating-chart", "online-clock", "school-bell", "attendance", "charades-cards"];

/** Footer (D-278): every feature page and the most used free tools by name, then the site's own pages. Every link is a page
 * of this language; a light language (tool pages only) has no feature column, and its other links lead to the English site (lp). */
export function PublicFooter() {
  const { features, tools } = marketingPagesFor(locale);
  const footerTools = FOOTER_TOOLS.flatMap((slug) => tools.filter((tool) => tool.slug === slug)).slice(0, 6);
  const group = (label: string, links: Array<[string, string]>, all?: [string, string]) => <nav aria-label={label}><strong>{label}</strong>{links.map(([href, text]) => <a key={href} href={href}>{text}</a>)}{all && <a className="footer-all" href={all[0]}>{all[1]} <span aria-hidden="true">→</span></a>}</nav>;
  return <footer className="public-footer">
    <div className="footer-brand-column">
      <a className="footer-brand" href={publicHref("/")}><BrandMark /><span><strong>{t("小森課堂")}</strong><small>{t("讓好表現被看見")}</small></span></a>
      {/* Same picker as the header (one control, not a list); hreflang in <head> keeps every language crawlable. */}
      <div className="footer-language"><LanguageSelector /></div>
    </div>
    <div className="footer-nav-groups">
      {features.length > 0 && group(t("功能介紹"), [...features.map((feature): [string, string] => [publicHref(featurePath(feature.slug)), feature.name])], [publicHref("/features"), t("看全部功能")])}
      {group(t("免費教師工具"), [...footerTools.map((tool): [string, string] => [publicHref(toolPath(tool.slug)), tool.name])], [publicHref("/tools"), t("看全部工具")])}
      {group(t("認識產品"), [[publicHref("/tour"), t("示範教室")], [publicHref("/pricing"), t("方案價格")], [publicHref("/updates"), t("最近更新")], [publicHref("/about"), t("關於我們")]])}
      {group(t("支援與帳號"), [[publicHref("/help"), t("幫助中心")], [publicHref("/articles"), t("教學專欄")], [publicHref("/contact"), t("聯絡我們")], [teacherHref("/login"), t("登入／註冊")]])}
      {group(t("法律說明"), [[publicHref("/privacy"), t("隱私政策")], [publicHref("/terms"), t("使用條款")]])}
    </div>
  </footer>;
}

/** Language menu: inside the teacher app the choice is also saved to the account (best effort). */
async function chooseLocale(next: Locale) {
  if (!publicTourPage && !isPublicSitePath(basePath)) {
    try { await api("/api/account/locale", { method: "PUT", body: JSON.stringify({ locale: next }) }); }
    catch { /* not signed in or offline: this device still switches */ }
  }
  switchLocale(next);
}

const LOCALE_NOTICE_KEY = "classmori-locale-notice-dismissed";
// Written in the suggested language (the visitor may not read the page language); English is the fallback.
const SUGGESTION_COPY: Partial<Record<Locale, { region: string; available: string; view: string; dismiss: string }>> = {
  "pt-BR": MARKET_CONTENT["pt-BR"].languageSuggestion,
  "tr": MARKET_CONTENT["tr"].languageSuggestion,
  "vi": MARKET_CONTENT["vi"].languageSuggestion,
  "id": MARKET_CONTENT["id"].languageSuggestion,

  /* i18n-ignore: static Gemini-translated copy */ "zh-Hans": { region: "语言", available: "这个页面有简体中文版", view: "查看简体中文版", dismiss: "关闭" },
  en: { region: "Language", available: "This page is available in English", view: "View in English", dismiss: "Dismiss" },
  es: { region: "Idioma", available: "Esta página está disponible en español", view: "Ver en español", dismiss: "Cerrar" },
  fr: { region: "Langue", available: "Cette page existe en français", view: "Voir en français", dismiss: "Fermer" },
};
/** Chinese public pages: offer the English site to visitors whose browser prefers it. Never redirects. */
function LocaleSuggestion() {
  const [target] = useState<Locale | null>(() => {
    if (!isDefaultLocale || location.hostname === "app.classmori.com" || !isPublicSitePath(basePath)) return null;
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    // A bare "zh" browser in mainland China, Singapore or Malaysia is offered Simplified Chinese (D-205).
    const preferred = localeFromLanguages(languages, browserTimezone());
    if (!preferred || preferred === locale || !pageExistsIn(preferred)) return null;
    // Someone who already chose Chinese (switcher, app language) is not asked again.
    if (savedLocale() === locale) return null;
    try { if (localStorage.getItem(LOCALE_NOTICE_KEY)) return null; } catch { /* storage blocked: still offer it */ }
    return preferred;
  });
  const [hidden, setHidden] = useState(false);
  if (!target || hidden) return null;
  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem(LOCALE_NOTICE_KEY, "1"); } catch { /* private mode */ }
  };
  // Deliberately in the visitor's language, not the page language.
  const copy = SUGGESTION_COPY[target] ?? SUGGESTION_COPY.en!;
  return <aside lang={LOCALE_CONFIG[target].htmlLang} aria-label={copy.region} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "4px 12px", padding: "8px 16px", borderBottom: "1px solid #cbdcc9", background: "#f5f9f0", color: "var(--forest-850)", fontSize: 14, lineHeight: 1.4 }}>
    <span>{copy.available}</span>
    <a href={pathInLocale(target)} onClick={(event) => { event.preventDefault(); switchLocale(target); }} style={{ color: "var(--forest-700)", fontWeight: 800 }}>{copy.view} <span aria-hidden="true">→</span></a>
    <button type="button" onClick={dismiss} aria-label={copy.dismiss} style={{ minWidth: 32, minHeight: 32, padding: 0, border: 0, background: "transparent", color: "var(--forest-850)", fontSize: 18, lineHeight: 1, cursor: "pointer" }}>×</button>
  </aside>;
}

// One header for every public page with a single account action (D-106): new and returning
// teachers both press 登入／註冊 and land on the same page (Google first, email on request).
export function PublicHeader({ className = "public-page-header" }: { className?: string }) {
  return <header className={className}>
    <a className="brand" href={publicHref("/")}><BrandMark />{t("小森課堂")}</a>
    <PublicNav />
    <a className="button nav-cta" href={teacherHref("/login")}>{t("登入／註冊")}</a>
    {/* Least important control last: language sits at the far right, after the account button. */}
    <LanguageSelector />
    <MarketingMobileMenu />
  </header>;
}

export function HelpPublicHeader() {
  return <PublicHeader className="public-page-header help-public-header" />;
}

/** Language picker (header, mobile menu, account settings): public pages move to the other URL, the app reloads. */
export function LanguageSelector({ compact = false }: { compact?: boolean }) {
  return <label className={`language-selector${compact ? " compact" : ""}`}>
    <span className="language-label">{t("語言")}</span>
    <select className="language-current" value={locale} aria-label={t("網站語言")} onChange={(event) => void chooseLocale(event.target.value as Locale)}>
      {(isPublicSitePath(basePath) ? LOCALES : FULL_LOCALES).map((item) => <option key={item} value={item} lang={LOCALE_CONFIG[item].htmlLang}>{LOCALE_CONFIG[item].label}</option>)}
    </select>
  </label>;
}

export function MarketingMobileMenu() {
  return <details className="marketing-mobile-menu">
    <summary aria-label={t("開啟網站選單")}><span /><span /><span /></summary>
    <nav aria-label={t("手機選單")}>
      <a className="mobile-menu-trial" href={teacherHref("/login")}>{t("登入／註冊")}</a>
      <NavLinks />
      <LanguageSelector compact />
    </nav>
  </details>;
}

// A lazily loaded chunk can fail (flaky network, or a deploy removed the old hashed file);
// show a reload prompt instead of unmounting the whole page.
export class ChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (!this.state.failed) return this.props.children;
    return <div className="fatal" role="alert">
      <h1>{t("這個畫面沒有載入完成")}</h1>
      <p>{t("可能是網路不穩，或小森課堂剛更新了版本。重新載入就好。")}</p>
      <button onClick={() => location.reload()}>{t("重新載入")}</button>
    </div>;
  }
}

export function LoadingView({ message }: { message: string }) {
  return <div className="loading forest-loading" role="status" aria-live="polite">
    <div className="forest-loading-card"><BrandMark /><strong>{t("小森課堂")}</strong><p>{message}</p><span className="forest-loading-track"><i /></span><small>{t("一起看見每份努力")}</small></div>
  </div>;
}
export type Snapshot = {
  class: {
    id: string;
    name: string;
    timezone: string;
    archived_at: string | null;
    pet_mode: PetMode;
    /** 1 unless the teacher turned the points ranking off (migration 0033); absent on older tour snapshots. */
    ranking_enabled?: number;
    /** 1 when this class lets reminders take points below zero (D-109). */
    allow_negative_points?: number;
    pet_hatch_points?: number;
    /** How the classroom lists reward and reminder reasons (customer batch §6). */
    reasons_layout?: "split" | "together";
    /** Pictures in the contact book are opt-in per class (migration 0065); absent in older snapshots = off. */
    contact_extras?: number;
    /** Automatic zhuyin in the contact book (migration 0066): opt-in per class; absent in older snapshots = off. */
    contact_zhuyin?: number;
    /** How new individual pets arrive (D-103): children claim them or the forest assigns one. */
    pet_pick_mode?: PetPickMode;
    /** 班級年級 (D-175): a canonical value of src/class-grade.ts; null = not chosen yet; absent in older tour snapshots. */
    grade?: string | null;
    /** 主要學科 (D-189): a code of src/class-subject.ts; null = not chosen; absent in older tour snapshots. */
    subject?: string | null;
    /** D-192: 1 when the class used 點名／聯絡簿／作業 in the last 14 days; absent in tour snapshots (= not used). */
    uses_attendance?: number;
    uses_contact?: number;
    uses_homework?: number;
  };
  students: Student[];
  /** species_slug is null while a child has not claimed the pet yet (D-103); picked is absent on older tour snapshots. */
  /** D-211 小屋裝飾: each child's decorations set out (appearance `equipped`); empty while decor is off or the plan is free. */
  petDecor?: Record<string, string[]>;
  individualPets: Array<{ id: string; student_id: string; name: string; species_slug: PetSpecies | null; picked?: boolean; xp: number; level: number; visualStage: number; nextThreshold: number | null; status: 'egg' | 'active'; /** D-214: the level has reached a form that waits for a tap (visualStage is the form still drawn). */ pendingEvolution?: boolean; evolveToStage?: number | null }>;
  archivedStudents: ArchivedStudent[];
  groups: Group[];
  tags: Tag[];
  /** Reasons the teacher turned off (names only), so 建議項目 does not offer them again (D-172). */
  retiredTags?: Array<{ name: string; polarity: "positive" | "negative" }>;
  pet: {
    id: string;
    name: string;
    status: string;
    species_slug: string | null;
    assigned_species_slug: string | null;
    xp: number;
    level: number;
    visualStage: number;
    nextThreshold: number | null;
    hatch_threshold: number;
  };
  preferences: {
    theme_id: string;
    density: string;
    motion_mode: string;
    scene_id: string;
    leaderboard_visible: number;
    board_layout: BoardModule[];
    idle_motion: PetIdleMotion;
    /** D-107; absent on snapshots cached before migration 0048 — read it through parseClassroomShortcuts. */
    classroom_shortcuts?: ClassroomShortcuts;
    /** D-273: 1 while the class's 學生專屬連結 are on (快速上手 progress); absent in older and tour snapshots. */
    student_view_enabled?: number;
    /** D-209: 1 while children may self-claim points on their link; absent in older snapshots. */
    student_view_self_claims_enabled?: number;
    /** D-217 給老師的回饋: the class switch (the menu shows 看學生回饋 while it is on). */
    student_view_feedback_enabled?: number;
  };
  store: {
    items: StoreItem[];
    pendingRedemptions: PendingRedemption[];
    /** Children's waiting requests (D-135); absent in snapshots cached before they existed. */
    studentRequests?: StudentRequest[];
  };
  /** Children's waiting self-claims (D-209) and who has one (for a stage layer). Absent in older snapshots. */
  selfClaims?: SelfClaimsSnapshot;
  /** 手動進化 (D-214): the class's way of evolving, and the children whose companion waits for a tap. Absent in older snapshots and the tour (= automatic). */
  evolution?: { mode: 'manual' | 'auto'; pendingEvolutionStudentIds: string[] };
  /** D-260: the shared pet graduates. `daysLeft` only while a shared class shows its notices; `graduation` is the keepsake after the switch. */
  sharedRetire?: { switchDay: string; daysLeft: number | null; graduation: SharedGraduation | null; seen: string[] };
  badges: Array<{ badge_key: string; earned_at: string | null }>;
  /** Unrevoked rewards in this class (absent in older snapshots and the public tour). */
  positiveActions?: number;
  /** region: the 國家／地區 the teacher chose (D-189), null = not chosen; regionEditable = owner or admin. Absent on older snapshots. */
  workspace: { market: "TW" | "HK"; region?: string | null; regionEditable?: boolean };
  /** `plan` and `freeLimits` come from D-090 snapshots; the public tour always says "teacher". */
  access: { state: string; workspaceState: string; endsAt: string | null; plan?: PlanTier; level?: "free" | "basic" | "plus"; limits?: { activeClassLimit: number; studentsPerClassLimit: number }; reward?: boolean; rewardKind?: "referral" | "gift" | null; freeLimits?: { activeClassLimit: number; studentsPerClassLimit: number }; overLimit?: OverLimit | null; addonsAvailable?: boolean };
  teacher: { displayName: string; email: string; locale: string; timezone?: string; hasPassword?: boolean; avatarVersion?: string | null; gradePromptDismissed?: boolean; gradePromptDue?: boolean };
};
export type DataRequestResult = { requestId: string; status: "requested"; notification: "accepted" | "pending" }
  | { requestId: string; status: "requested"; flow: "export"; duplicate: boolean; state: "waiting" | "ready" | "manual"; email: string; hours: string };
export const dataRequestFeedback = (type: "export" | "deletion", result: DataRequestResult) => {
  const id = result.requestId;
  if ("flow" in result) return result.duplicate
    ? result.state === "ready" ? t("你已經有一筆可以下載的資料，請到「設定 → 資料與隱私」下載。")
      : result.state === "manual" ? t("你的資料申請需要客服協助，我們會另外聯絡你。")
      : t("你已經有一筆整理中的資料申請，完成後會寄到 {email}。", { email: result.email })
    : t("已收到申請。我們需要整理一下資料，預計 {hours} 小時內寄到 {email}。信裡只有連結，沒有班級資料；收到後 {days} 天內可以到「資料與隱私」下載，最多 {count} 次。", { hours: result.hours, email: result.email, days: EXPORT_WINDOW_DAYS, count: EXPORT_MAX_DOWNLOADS });
  if (result.notification !== "accepted") return type === "export"
    ? t("已記錄資料匯出申請（編號 {id}），但郵件通知暫時未送出，系統會重試。若急需處理，請寄信至 support@classmori.com。", { id })
    : t("已記錄資料刪除申請（編號 {id}），但郵件通知暫時未送出，系統會重試。若急需處理，請寄信至 support@classmori.com。", { id });
  return type === "export"
    ? t("已記錄資料匯出申請（編號 {id}），郵件服務已接收通知；團隊會先核對身分與範圍。", { id })
    : t("已記錄資料刪除申請（編號 {id}），郵件服務已接收通知；團隊會先核對身分與範圍。目前尚未刪除資料。", { id });
};
/** Trial, teacher plan and a free-plan class within the free limits can score and edit (D-090). */
export const isClassWritable = (data: Snapshot) => isWritableState(data.access.state) && !data.class.archived_at;
/** A free-plan class over the limit may still archive students to fit. */
export const canArchiveStudents = (data: Snapshot) => canTidyClass(data.access.state, Boolean(data.class.archived_at));
export const petFormLabel = (visualStage: number) => visualStage >= 30 ? t('最終形態 4/4') : visualStage >= 20 ? t('第三形態 3/4') : visualStage >= 10 ? t('第二形態 2/4') : t('初始形態 1/4');
/** Chinese source key; display it with t(petLevelExplanation) or petLevelExplanationText(). */
export const petLevelExplanation = msg('小窩／蛋是出場前的 Lv.0；Lv.1–9 為初始形態，Lv.10／20／30 才改變形態。');
export const petLevelExplanationText = () => t(petLevelExplanation);
export type HistoryItem = {
  id: string;
  reason_snapshot: string;
  points_snapshot: number;
  polarity?: Tag['polarity'];
  created_at: string;
  undone_at: string | null;
  /** Why it was undone; "needs_correction" = taken back until the correction is signed (D-161). */
  undo_reason?: string | null;
  student_names: string;
  student_count: number;
  operator_name: string;
  session_action?: boolean;
  settlement_id?: string | null;
  completion_instance_id?: string | null;
  completion_batch_id?: string | null;
  completion_canceled?: number;
};
export type StoreItem = {
  id: string;
  name: string;
  emoji: string | null;
  points: number;
  stock: number | null;
  is_active: number;
  sort_order: number;
  /** 舊版演示資料可能沒有這兩個欄位，缺少時視為一般獎勵。 */
  reward_kind?: RewardKind;
  prizes?: SurprisePrize[];
  /** Changes when a prize is handed out or put back; an edit sends it back so an older screen cannot overwrite the counts. */
  prizes_version?: string | null;
};
import type { OpenRequest as StudentRequest } from "../../src/store";
export type { StudentRequest };
export type PendingRedemption = {
  id: string;
  student_id: string;
  student_name: string;
  item_id: string;
  item_name_snapshot: string;
  prize_name_snapshot?: string | null;
  prize_emoji_snapshot?: string | null;
  prize_rarity_snapshot?: PrizeRarity | null;
  points_spent: number;
  created_at: string;
};
export type RedemptionRecord = PendingRedemption & { status: "pending" | "fulfilled" | "rejected"; handled_at: string | null; /** D-211: set when this record is a home decor purchase (already 已發放). */ decor_item_id?: string };
export type BadgeDef = {
  key: string;
  name: string;
  emoji: string;
  description: string;
  kind: BadgeKind;
  threshold: number;
};
/** Names and descriptions are Chinese source keys; badgesForMode() returns them translated. */
export const BADGES: BadgeDef[] = [
  { key: "badge-hatched", name: msg("初次相遇"), emoji: "🌱", description: msg("班寵和全班第一次見面"), kind: "hatch", threshold: 0 },
  { key: "badge-level-5", name: msg("穩定成長"), emoji: "🌱", description: msg("班寵達到等級 5"), kind: "level", threshold: 5 },
  { key: "badge-level-10", name: msg("茁壯夥伴"), emoji: "🌿", description: msg("班寵達到等級 10"), kind: "level", threshold: 10 },
  { key: "badge-level-15", name: msg("森林常客"), emoji: "🍃", description: msg("班寵達到等級 15"), kind: "level", threshold: 15 },
  { key: "badge-level-20", name: msg("可靠同伴"), emoji: "🌳", description: msg("班寵達到等級 20"), kind: "level", threshold: 20 },
  { key: "badge-level-25", name: msg("傳說班寵"), emoji: "🌟", description: msg("班寵達到等級 25"), kind: "level", threshold: 25 },
  { key: "badge-level-30", name: msg("滿級榮光"), emoji: "🏆", description: msg("班寵達到等級 30"), kind: "level", threshold: 30 },
  { key: "badge-actions-50", name: msg("五十次肯定"), emoji: "💝", description: msg("全班累積 50 次具體肯定"), kind: "actions", threshold: 50 },
  { key: "badge-actions-200", name: msg("兩百次肯定"), emoji: "💖", description: msg("全班累積 200 次具體肯定"), kind: "actions", threshold: 200 },
  { key: "badge-actions-500", name: msg("五百次肯定"), emoji: "💫", description: msg("全班累積 500 次具體肯定"), kind: "actions", threshold: 500 },
  { key: "badge-companion-first", name: msg("第一位夥伴"), emoji: "🐣", description: msg("第一隻夥伴從小窩或蛋裡出場"), kind: "companion-out", threshold: 1 },
  { key: "badge-companion-evolved", name: msg("第一次進化"), emoji: "🌿", description: msg("第一隻夥伴進化到第二形態"), kind: "companion-evolved", threshold: 1 },
  { key: "badge-companion-half", name: msg("一起長大"), emoji: "🌳", description: msg("一半同學的夥伴進化到第二形態"), kind: "companion-half", threshold: 0 },
  { key: "badge-companion-final", name: msg("最終形態"), emoji: "🏆", description: msg("第一隻夥伴達到最終形態"), kind: "companion-final", threshold: 1 },
];
/** Badges that belong to this classroom's mode: shared ones plus class-pet or companion milestones. */
export const badgesForMode = (mode: PetMode): BadgeDef[] => BADGES.filter((badge) => badgeAppliesTo(badge.kind, mode))
  .map((badge) => ({ ...badge, name: t(badge.name), description: t(badge.description) }));
export const badgeIcon = (badge: BadgeDef): ForestIconName => {
  if (badge.kind === "hatch" || badge.kind === "companion-out") return "sprout";
  if (badge.kind === "companion-evolved") return "acorn";
  if (badge.kind === "companion-half") return "tree-rings";
  if (badge.kind === "companion-final") return "celebrate";
  if (badge.kind === "actions") return badge.threshold >= 500 ? "celebrate" : badge.threshold >= 200 ? "cooperate" : "heart";
  return badge.threshold >= 30 ? "celebrate" : badge.threshold >= 25 ? "star" : badge.threshold >= 20 ? "tree-rings" : badge.threshold >= 15 ? "leaf-calendar" : badge.threshold >= 10 ? "acorn" : "sprout";
};
export type RoutineSummary = {
  id: string;
  name: string;
  emoji: string | null;
  sort_order: number;
  doneCount: number;
  absentCount: number;
  denominator: number;
  completed: boolean;
};
export type RoutineBoardStudent = {
  id: string;
  seat_no: string | null;
  display_name: string;
  avatar_key: string;
  group_id: string | null;
  color_key: string | null;
  status: "done" | "absent" | null;
};
export type RoutineBoardData = {
  routine: { id: string; name: string; emoji: string | null };
  day: string;
  students: RoutineBoardStudent[];
  doneCount: number;
  absentCount: number;
  denominator: number;
  completed: boolean;
  xpGrantedToday: boolean;
  petProgressGranted?: boolean;
  snapshot?: Snapshot;
};
export type TourRoutineDemo = { day: string; routines: RoutineSummary[]; boards: RoutineBoardData[] };
export type Session = {
  authenticated?: boolean;
  market?: "TW" | "HK";
  /** 國家／地區 of the teacher's own workspace (D-189); null = not chosen. */
  region?: string | null;
  /** D-204: the country of this request's IP (Cloudflare), for the region guess; null in local dev. */
  ipCountry?: string | null;
  teacher: { displayName: string; email: string; locale: string; uiLocale?: string | null };
  class: ClassItem | null;
  classes: ClassItem[];
  access: { state: string; endsAt: string | null } | null;
  /** Set while the account deletion is pending (D-080); the app shows only the restore screen. */
  deletion?: { requestedAt: string; purgeAfter: string };
};
export type ClassItem = {
  id: string;
  name: string;
  workspace_id: string;
  archived_at: string | null;
  /** 班級年級 (D-175); null = not chosen yet. */
  grade?: string | null;
  /** The teacher's role in this class (a viewer cannot change it). */
  role?: "owner" | "editor" | "viewer";
  student_count: number;
  /** UTC time an archived class will be purged; null unless its deletion is pending. */
  deletion_purge_after?: string | null;
};
export type ImportMapping = {
  seatNo: number;
  displayName: number;
  avatarHint: number;
  groupName: number;
};
export type ImportError = { row: number; field: string; code: string };
/** A record of Chinese source keys that reads back translated, so module-level lookup tables stay safe. */
export function translatedRecord<T extends Record<string, string>>(labels: T): T {
  return new Proxy(labels, {
    get: (target, key, receiver) => {
      const value = Reflect.get(target, key, receiver);
      return typeof value === "string" ? t(value) : value;
    },
  });
}
export const importErrorLabels: Record<string, string> = translatedRecord({
  invalid_name: msg("姓名不可空白，且最多 30 字"),
  duplicate_name: msg("匯入名單內姓名重複"),
  existing_name: msg("班內已有同名學生"),
  duplicate_seat: msg("匯入名單內座號／學號重複"),
  seat_too_long: msg("座號／學號最多 32 個字"),
  existing_seat: msg("座號／學號已存在於班內"),
});
export const importWarningLabels: Record<string, string> = translatedRecord({
  seat_blank: msg("未填座號／學號，仍可匯入"),
  avatar_hint_unknown: msg("頭像提示無法辨識，將使用不限頭像"),
  new_group: msg("小組尚未建立，請確認是否一併建立"),
});
export const importFieldLabels: Record<string, string> = translatedRecord({ displayName: msg("姓名"), seatNo: msg("座號／學號"), avatarHint: msg("頭像提示"), groupName: msg("小組") });
export const seatLabel = (locale: string) => locale === "zh-HK" ? t("學號") : t("座號／學號");
export type ApiResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: { code: string; message: string; details?: unknown };
      requestId: string;
    };

export type SharedGraduation = { petName: string; species: string | null; level: number; visualStage: number; classPoints: number; graduatedAt: string; bySystem: boolean };
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // /tour answers everything but its snapshot in memory. Elsewhere (the home-page live demo) any
  // class request while a demo classroom is open stays in memory too; the teacher app never has one.
  if (publicTourPage ? !(path.startsWith("/api/public-tour?") && (!init?.method || init.method === "GET")) : tourSessions.size > 0 && path.startsWith("/api/classes/")) {
    const classId = /^\/api\/classes\/([^/?]+)/.exec(path)?.[1];
    const session = classId ? tourSessions.get(classId) : undefined;
    if (!session) throw new Error(t("這項操作需在自己的教室使用；示範教室不會送出帳號、付款或資料申請。"));
    const result = await session.handle<T>(path, init);
    // Tour visitors' actions become Clarity custom events (fictional data, no values sent).
    const event = tourEventName(path, (init?.method ?? "GET").toUpperCase());
    if (event) clarityEvent(event);
    return result;
  }
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        ...(init?.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}),
        // The Worker answers errors in this language (see src/i18n/server.ts).
        [LOCALE_HEADER]: locale,
        ...init?.headers,
      },
    });
  } catch {
    // Browsers report offline as "Failed to fetch"/"Load failed"; no status means
    // the receipt is unknown, so callers keep their retry-the-same-op path.
    throw new Error(t("網路連線中斷，這次操作可能尚未儲存"));
  }
  let payload: ApiResult<T>;
  try {
    payload = (await response.json()) as ApiResult<T>;
  } catch {
    // A platform error page (502/524…) is HTML, not our JSON envelope.
    const error = new Error(t("伺服器暫時沒有回應，請稍後再試")) as Error & { status?: number };
    if (!response.ok) error.status = response.status;
    throw error;
  }
  if (!payload.ok) {
    const separator = payload.error.message.indexOf("。／");
    // The Worker already translated non-Chinese errors; only the Chinese UI picks zh-TW or zh-HK.
    const locale = preferenceStorage.getItem("classmori-locale");
    const localizedMessage = separator >= 0 && isDefaultLocale
      ? locale === "zh-HK"
        ? payload.error.message.slice(separator + 2)
        : payload.error.message.slice(0, separator + 1)
      : payload.error.message;
    const error = new Error(localizedMessage) as Error & {
      code?: string;
      details?: unknown;
      status?: number;
    };
    error.status = response.status;
    error.code = payload.error.code;
    error.details = payload.error.details;
    // Teacher-plan and quota errors also surface one upgrade prompt in the workspace (plan-ui.tsx).
    if (isPlanErrorCode(error.code) && typeof dispatchEvent === "function")
      dispatchEvent(new CustomEvent<PlanErrorDetail>(PLAN_ERROR_EVENT, { detail: { code: error.code, message: localizedMessage } }));
    throw error;
  }
  return payload.data;
}

export const uid = () => crypto.randomUUID();
export const UNGROUPED = "__ungrouped";
export const avatarUrl = (key: string) => `/assets/optimized/v1/avatars/${key}.webp`;
export const avatarSrcSet = (key: string) => `/assets/optimized/v1/avatars/${key}-96.webp 1x, /assets/optimized/v1/avatars/${key}-160.webp 2x, ${avatarUrl(key)} 3x`;
/** Image props for a student's face: the uploaded photo when there is one (D-112), else the preset avatar. */
export const studentAvatarProps = (student: { avatar_key: string; photo_url?: string | null }) =>
  student.photo_url ? { src: student.photo_url } : { src: avatarUrl(student.avatar_key), srcSet: avatarSrcSet(student.avatar_key) };
export const teacherAvatarUrl = (version: string) => `/api/account/avatar?v=${encodeURIComponent(version)}`;
export function TeacherAvatar({ teacher, className }: { teacher: Snapshot["teacher"]; className: string }) {
  return teacher.avatarVersion
    ? <img className={className} src={teacherAvatarUrl(teacher.avatarVersion)} alt="" aria-hidden="true" />
    : <span className={className}>{teacher.displayName.slice(0, 1)}</span>;
}
/** Species display names, translated on read (PET_SPECIES_NAMES holds the Chinese keys). */
export const speciesLabels: Record<string, string> = translatedRecord(PET_SPECIES_NAMES);
export const legacyPetSpecies = new Set<string>(PET_SPECIES.slice(0, 10));
export const isEvolutionSpecies = (species: string): species is keyof typeof petEvolutionData => Object.prototype.hasOwnProperty.call(petEvolutionClipUrls, species);
// Clip paths are fetched per species on first use (≈30 KB each) instead of shipping all
// 40 species (~1.2 MB) in the entry bundle; the promise cache dedupes concurrent requests.
export type EvolutionSheet = (typeof petEvolutionData)[keyof typeof petEvolutionData];
export const evolutionSheets = new Map<string, EvolutionSheet>();
export const evolutionSheetRequests = new Map<string, Promise<EvolutionSheet>>();
export function loadEvolutionSheet(species: string): Promise<EvolutionSheet> {
  let request = evolutionSheetRequests.get(species);
  if (!request) {
    request = fetch(petEvolutionClipUrls[species]).then((response) => {
      if (!response.ok) throw new Error(`clip ${species} ${response.status}`);
      return response.json() as Promise<EvolutionSheet>;
    }).then((sheet) => { evolutionSheets.set(species, sheet); return sheet; });
    request.catch(() => evolutionSheetRequests.delete(species));
    evolutionSheetRequests.set(species, request);
  }
  return request;
}
// Offscreen art (card walls, long rosters, later homepage sections) waits until it is
// within 300px of the viewport before fetching its clip data and sheet image.
export function useNearViewport<T extends Element>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (near) return;
    const element = ref.current;
    if (!element || !("IntersectionObserver" in window)) { setNear(true); return; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { setNear(true); observer.disconnect(); }
    }, { rootMargin: "300px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [near]);
  return [ref, near];
}
export function useEvolutionSheet(species: string | null) {
  const [sheet, setSheet] = useState(() => species ? evolutionSheets.get(species) : undefined);
  useEffect(() => {
    if (!species) return;
    const ready = evolutionSheets.get(species);
    if (ready) { setSheet(ready); return; }
    let live = true;
    loadEvolutionSheet(species).then((value) => { if (live) setSheet(value); }).catch(() => {});
    return () => { live = false; };
  }, [species]);
  return sheet;
}
export const evolutionStageIndex = (stage: number) => stage >= 30 ? 4 : stage >= 20 ? 3 : stage >= 10 ? 2 : stage >= 1 ? 1 : 0;
export function PetEvolutionArt({ species, visualStage = 1, className = '', label, anchor = 'center' }: { species: keyof typeof petEvolutionData; visualStage?: number; className?: string; label?: string; /** 'bottom' stands the pet on the floor of its box (growth-scaled stages). */ anchor?: 'center' | 'bottom' }) {
  const [placeholder, near] = useNearViewport<HTMLSpanElement>();
  const sheet = useEvolutionSheet(near || evolutionSheets.has(species) ? species : null);
  const clipId = `pet-evolution-${React.useId().replace(/:/g, '')}`;
  // Only the form on screen is downloaded (~50 KB, not the ~200 KB five-form sheet). When the
  // pet changes form, the old form stays until the new file has arrived, so it never blanks.
  const target = evolutionStageIndex(visualStage);
  const [shown, setShown] = useState({ species: species as string, index: target });
  const formIndex = shown.species === species ? shown.index : target;
  useEffect(() => {
    if (shown.species === species && shown.index === target) return;
    if (shown.species !== species || loadedPetForms.has(petFormUrl(species, target))) { setShown({ species, index: target }); return; }
    let live = true;
    void loadPetForm(petFormUrl(species, target)).then(() => { if (live) setShown({ species, index: target }); });
    return () => { live = false; };
  }, [species, target, shown]);
  // Same box while loading, so rosters and stages do not jump.
  if (!sheet) return <span ref={placeholder} className={`pet-evolution-art pet-evolution-loading ${className}`} role="img" aria-label={label ?? speciesLabels[species] ?? t('寵物')} />;
  const stage = sheet.stages[formIndex];
  const [x, y, width, height] = formBox(stage.clipPath, sheet.width, sheet.height);
  return <svg className={`pet-evolution-art ${className}`} data-pet-clip={`${species}-${formIndex}`} viewBox={stage.viewBox} role="img" aria-label={label ?? speciesLabels[species] ?? t('寵物')} preserveAspectRatio={anchor === 'bottom' ? 'xMidYMax meet' : 'xMidYMid meet'}>
    <defs><clipPath id={clipId} clipPathUnits="userSpaceOnUse"><path d={stage.clipPath} /></clipPath></defs>
    <image href={petFormUrl(species, formIndex)} x={x} y={y} width={width} height={height} clipPath={`url(#${clipId})`} onLoad={() => loadedPetForms.add(petFormUrl(species, formIndex))} />
  </svg>;
}
export const petAsset = (path: string) => `${path}?v=pets-20260926`;
export const atlasFile = (slug: string) => petAsset(`/assets/pet-atlases/${['rabbit','penguin','fox','corgi','cat'].includes(slug) ? `${slug}-6-actions` : slug}.webp`);
export const evolvedGestureAtlasSpecies = new Set(['fox', 'corgi', 'cat', 'red-panda', 'panda', 'capybara', 'owl']);
export type PetAtlasProps = { species: string; action?: 'idle' | 'greet' | 'hop' | 'celebrate' | 'surprise' | 'sleepy'; className?: string; label?: string; visualStage?: number; animated?: boolean; sceneSurface?: boolean; evolutionOnly?: boolean };
export function PetAtlas(props: PetAtlasProps) {
  if ((props.evolutionOnly || !legacyPetSpecies.has(props.species)) && isEvolutionSpecies(props.species)) return <PetEvolutionArt species={props.species} visualStage={props.visualStage} className={props.className} label={props.label} />;
  return <LegacyPetAtlas {...props} />;
}
export function LegacyPetAtlas({ species, action = 'idle', className = '', label, visualStage = 1, animated = false, sceneSurface = false }: PetAtlasProps) {
  const surface = usePetAtlasSurface(atlasFile(species), sceneSurface && visualStage < 10);
  const cells = { idle: [0,0], greet: [50,0], hop: [100,0], celebrate: [0,100], surprise: [50,100], sleepy: [100,100] };
  const [x,y] = cells[action];
  if (visualStage >= 10) {
    if (evolvedGestureAtlasSpecies.has(species)) return <span className={`pet-atlas pet-form pet-form-atlas ${className}`} role="img" aria-label={label ?? speciesLabels[species] ?? t('寵物')} data-pose={action} data-animated={animated} style={{ backgroundImage: `url(${petAsset(`/assets/pet-forms/${species}/stage-${visualStage}-6-actions.webp`)})`, backgroundPosition: `${x}% ${y}%` }} />;
    return <img className={`pet-form ${className}`} src={petUrl(species, visualStage)} alt={label ?? speciesLabels[species] ?? t('寵物')} data-pose={action} data-animated={animated} draggable={false} />;
  }
  if (sceneSurface && visualStage < 10 && !surface.url) return <span className="pet-atlas-loading" role={surface.error ? 'alert' : 'status'}>{surface.error ? t('夥伴圖片載入失敗，請重新整理頁面') : t('夥伴準備中…')}</span>;
  if (species === 'otter') return <img className={className} src={petUrl('otter')} alt={label ?? ''} />;
  return <span className={`pet-atlas ${className}`} role="img" aria-label={label ?? (speciesLabels[species] ?? t('寵物')) + (action === 'idle' ? '' : `・${action}`)} style={{ backgroundImage: `url(${surface.url})`, backgroundPosition: `${x}% ${y}%` }} />;
}

/** An individual pet waiting for its child to claim it (D-103); shows no species. */
export const MYSTERY_EGG_URL = "/assets/pet-pick/mystery-egg.webp";
export function PetVisual({ species, className = '', alt = '', visualStage = 1, shared = false, sceneSurface = false, anchor }: { species: string | null; className?: string; alt?: string; visualStage?: number; shared?: boolean; sceneSurface?: boolean; anchor?: 'center' | 'bottom' }) {
  // No species: the whole-class egg, or an individual pet a child has not claimed yet (D-103).
  if (!species) return <img className={className} src={shared ? "/assets/forest-pets/egg.webp" : MYSTERY_EGG_URL} alt={alt} />;
  if (shared && visualStage === 0 && legacyPetSpecies.has(species)) return <img className={className} src="/assets/forest-pets/egg.webp" alt={alt} />;
  if (shared && isForestSpecies(species)) return <img className={className} src={petUrl(species, visualStage)} alt={alt} />;
  if (shared && legacyPetSpecies.has(species)) return <LegacyPetAtlas species={species} className={className} label={alt || undefined} visualStage={visualStage} sceneSurface={sceneSurface} />;
  if (isEvolutionSpecies(species)) return <PetEvolutionArt species={species} className={className} label={alt || undefined} visualStage={visualStage} anchor={anchor} />;
  return <img className={className} src={petUrl(species, visualStage)} alt={alt} />;

}
/** `preferPhoto`: roster-style places show an uploaded photo even in individual-pet classes (D-112). */
export function StudentIdentity({ student, data, alt = '', preferPhoto = false }: { student: Pick<Student, 'id' | 'display_name' | 'avatar_key'>; data: Snapshot; alt?: string; preferPhoto?: boolean }) {
  const hasPhoto = Boolean(data.students.find((item) => item.id === student.id)?.photo_url);
  if (data.class.pet_mode === 'individual' && !(preferPhoto && hasPhoto)) {
    const pet = data.individualPets.find((item) => item.student_id === student.id);
    if (pet) return <PetVisual species={pet.species_slug} visualStage={pet.status === 'egg' ? 0 : pet.visualStage} className="student-identity" alt={alt} />;
  }
  // The prop may be a slim copy; the class snapshot carries the photo.
  const photo_url = data.students.find((item) => item.id === student.id)?.photo_url ?? null;
  return <img className={`student-identity${photo_url ? " student-photo" : ""}`} {...studentAvatarProps({ avatar_key: student.avatar_key, photo_url })} alt={alt} />;
}
/** 可兌換 beside a student's points (D-179): only while it differs from the points, i.e. after the child redeemed something. */
export const spendableBeside = (student: Pick<Student, 'points_earned' | 'points_available'>) => (student.points_available ?? 0) !== growthPointsOf(student) ? student.points_available ?? 0 : null;
/** `dense` (D-252, classes over 24): seat number set apart instead of 「 · 」 and one points line, so 可兌換 moves into the hover title instead of wrapping the card. */
export function ClassroomStudentChoice({ student, data, selected, disabled = false, rank, dense = false, onClick }: { student: Student; data: Snapshot; selected: boolean; disabled?: boolean; rank?: number; dense?: boolean; onClick: () => void }) {
  const points = growthPointsOf(student), spendable = spendableBeside(student);
  // D-253: the biggest mark past the final form, small, beside the points.
  const pet = data.class.pet_mode === 'individual' ? data.individualPets.find((item) => item.student_id === student.id) : undefined;
  const marks = pet && pet.status !== 'egg' ? shownMarks(pet.level, pet.visualStage) : 0;
  const label = rank ? `#${rank}` : student.seat_no ?? '—';
  const spendableText = spendable === null ? null : t("可兌換 {count}", { count: spendable });
  return <button type="button" className={`classroom-student-choice${selected ? ' selected' : ''}`} aria-label={spendable === null ? t("{label} · {name} {count} 點", { label, name: student.display_name, count: points }) : t("{label} · {name} {count} 點 · 可兌換 {spendable} 點", { label, name: student.display_name, count: points, spendable })} aria-pressed={selected} disabled={disabled} onClick={onClick} title={`${student.seat_no ?? '—'} · ${student.display_name}${dense ? ` · ${t("{count} 點", { count: points })}${spendableText ? ` · ${spendableText}` : ''}` : ''}`}>
    <StudentIdentity student={student} data={data} />
    <span>{dense ? <strong><b className="roster-seat">{label}</b>{student.display_name}</strong> : <strong>{label} · {student.display_name}</strong>}<small className={spendableText && !dense ? "points-with-total" : undefined}>{t("{count} 點", { count: points })}{spendableText && !dense && <> · <span className="spendable-beside">{spendableText}</span></>}{marks > 0 && <span aria-hidden="true"><PetMarks steps={marks} compact className="roster-marks" /></span>}</small></span>
    <i className="selection-mark" aria-hidden="true" />
  </button>;
}
export const SharedStudentChoice = ClassroomStudentChoice;
export const IndividualStudentChoice = ClassroomStudentChoice;
export const petUrl = (slug: string | null, visualStage = 1) => {
  if (!slug) return "/assets/forest-pets/egg.webp";
  if (visualStage >= 10) return petAsset(`/assets/pet-forms/${slug}/stage-${visualStage}.webp`);
  if (slug === "otter") return "/assets/forest-pets/media/2f1ed86008716ef8.webp";
  if (slug === "rabbit") return "/assets/forest-pets/media/3a50e654b48b512b.webp";
  if (slug === "penguin") return "/assets/forest-pets/media/658383ae2283bd24.webp";
  return `/assets/pets/${slug}-stage-01.webp`;
};
export const habitatImages: Record<string, string> = {
  forest: "9a6a3168d233f3d6", creek: "2c395dd7c8b068c3",
  garden: "39473430a317107d", bay: "51cdc0882c7d0a9b",
};
export const habitatStyle = (scene: string): React.CSSProperties => ({
  backgroundImage: `url(/assets/forest-pets/media/${habitatImages[scene] ?? habitatImages.forest}.webp)`,
});
/** Labels are Chinese source keys, translated where rendered. */
export const HABITAT_OPTIONS = [
  ["forest", msg("森林小徑"), "49b458435bdae76f"],
  ["creek", msg("溪畔草地"), "5f99f46156078373"],
  ["garden", msg("花園樹屋"), "7c6aeb3a2403e12e"],
  ["bay", msg("暖陽海灣"), "6a68be26f95af045"],
] as const;
// One picker for both modes: the shared class-pet settings and the individual stage backdrop.
// `locked` scenes (teacher plan, D-090) show a lock and cannot be picked; the current one stays shown.
export function HabitatChoices({ value, onChange, locked }: { value: string; onChange: (id: string) => void; locked?: (id: string) => boolean }) {
  return <div className="habitat-choices">{HABITAT_OPTIONS.map(([id, label, image]) => {
    const isLocked = Boolean(locked?.(id));
    return <button key={id} type="button" aria-pressed={value === id} className={`${value === id ? "active" : ""}${isLocked ? " plan-locked" : ""}`} disabled={isLocked && value !== id} aria-label={isLocked ? t("{name}（標準版）", { name: t(label) }) : undefined} onClick={() => { if (!isLocked) onChange(id); }}><img src={`/assets/forest-pets/media/${image}.webp`} alt="" loading="lazy" width="240" height="135" /><span>{t(label)}{value === id && <span className="habitat-selected">{t("已選擇")}</span>}{isLocked && <span className="plan-lock-badge">{t("標準版")}</span>}</span></button>;
  })}</div>;
}
export const isForestSpecies = (slug: string | null | undefined): slug is ForestPetSpecies =>
  slug === "otter" || slug === "rabbit" || slug === "penguin";
// New classes start with one pet per student preselected and marked as recommended;
// the shared pet stays an equal choice, and teachers can switch later in class settings.
export function PetModeChoices({ value, onChange }: { value: PetMode | null; onChange: (value: PetMode) => void }) {
  return <fieldset className="pet-mode-choices"><legend>{t("這個班要怎麼養夥伴？")}</legend>
    <label className={value === 'individual' ? 'selected' : ''}><input type="radio" name="pet-mode" checked={value === 'individual'} onChange={() => onChange('individual')} /><strong>{t("一人養一隻")} <span className="pet-mode-recommended">{t("推薦")}</span></strong><PetEvolutionArt species="red-panda" visualStage={10} label={t("小熊貓")} /><small>{t("每個孩子有自己的夥伴，靠自己的點數長大。")}</small></label>
    <label className={value === 'class' ? 'selected' : ''}><input type="radio" name="pet-mode" checked={value === 'class'} onChange={() => onChange('class')} /><strong>{t("全班養一隻")}</strong><img src={petAsset("/assets/pet-forms/otter/stage-10.webp")} alt="" /><small>{t("全班一起養一隻，所有人的點數加總讓牠長大，適合培養共同目標。")}</small></label>
    <p>{t("兩種養法都能加分、提醒扣分和兌換；提醒扣分可能讓寵物降級，兌換不會。之後可在「設定 → 寵物設定」隨時切換，點數不會改變。")}</p>
  </fieldset>;
}
/** Workspace labels by teacher market; values are Chinese keys that read back translated. */
export const locales = {
  "zh-TW": translatedRecord({
    classroom: msg("課堂"),
    routine: msg("每日事項"),
    insights: msg("成長看板"),
    tools: msg("課堂工具"),
    students: msg("學生"),
    log: msg("點數紀錄"),
    personal: msg("個人化"),
    settings: msg("班級設定"),
    all: msg("全班"),
    selected: msg("已選擇"),
    save: msg("儲存"),
    picker: msg("隨機點名"),
    homework: msg("作業"),
  }),
  "zh-HK": translatedRecord({
    classroom: msg("課堂"),
    routine: msg("每日事項"),
    insights: msg("成長看板"),
    tools: msg("課堂工具"),
    students: msg("學生"),
    log: msg("點數紀錄"),
    personal: msg("個人化"),
    settings: msg("班級設定"),
    all: msg("全班"),
    selected: msg("已選擇"),
    save: msg("儲存"),
    picker: msg("隨機抽籤"),
    homework: msg("功課"),
  }),
};

/** Homepage classroom tools; a function so names and descriptions are translated at render. */
export const homeToolHighlights = () => [
  { id: "picker", name: t("抽籤點名"), description: t("一次抽一位或好幾位，這一輪不會重複；也能抽全班共享的獎勵。"), icon: "picker" },
  { id: "timer", name: t("倒數計時"), description: t("切到別的分頁，也照樣準時結束。"), icon: "hourglass" },
  { id: "groups", name: t("隨機分組"), description: t("臨時分組一秒完成，不會打亂你原本的小組。"), icon: "shuffle" },
  { id: "instructions", name: t("課堂指令板"), description: t("把「現在要做什麼」投上大螢幕，最多六個步驟，孩子自己跟著做。"), icon: "instruction" },
] as const;


export const marketingHost = ["classmori.com", "localhost", "127.0.0.1"].includes(location.hostname) &&
  !(["localhost", "127.0.0.1"].includes(location.hostname) && location.pathname.startsWith("/class/"));
